import React, { useRef, useState, useEffect } from "react";
import {
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Fade,
  Tooltip,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import axios from "axios";
import { tokens } from "../theme";
import { useTheme } from "@mui/material/styles";

const PictureEditor = ({
  initialImage,
  onImageChange,
  size = 100,
  type = "circle",
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(initialImage || null);
  const [croppedImage, setCroppedImage] = useState(initialImage);
  const [cropRect, setCropRect] = useState({
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  });
  const [renderedImage, setRenderedImage] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [dragType, setDragType] = useState(null);
  const [containerSize, setContainerSize] = useState({
    width: 400,
    height: 400,
  });
  const inputRef = useRef();
  const imageRef = useRef();
  const [isSubmit, setIsSubmit] = useState(false);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [hovered, setHovered] = useState(false);

  const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

  const openFileSelector = () => inputRef.current.click();

  const getAvatarSx = () => {
    switch (type) {
      case "rounded":
        return { width: size, height: size, borderRadius: 8 };
      case "square":
        return { width: size, height: size, borderRadius: 0 };
      case "circle":
      default:
        return { width: size, height: size, borderRadius: "50%" };
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setDialogOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const startDragging = (type) => {
    setDragging(true);
    setDragType(type);
  };

  const stopDragging = () => {
    setDragging(false);
    setDragType(null);
  };

  const handleMouseMove = (e) => {
    if (!dragging || !renderedImage) return;
    const container = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - container.left;
    const mouseY = e.clientY - container.top;
    const { width: imgW, height: imgH } = renderedImage;

    const x = clamp(mouseX, 0, imgW);
    const y = clamp(mouseY, 0, imgH);

    const minCrop = imgW * 0.1;
    const maxCrop = imgW * 0.8;

    setCropRect((prev) => {
      let { x: px, y: py, width, height } = prev;
      switch (dragType) {
        case "move":
          return {
            ...prev,
            x: clamp(x - width / 2, 0, imgW - width),
            y: clamp(y - height / 2, 0, imgH - height),
          };
        case "resize-br": {
          const newSize = clamp(Math.min(x - px, y - py), minCrop, maxCrop);
          return { ...prev, width: newSize, height: newSize };
        }
        case "resize-tl": {
          const newSize = clamp(
            Math.min(width + (px - x), height + (py - y)),
            minCrop,
            maxCrop
          );
          const newX = clamp(px + (width - newSize), 0, imgW - newSize);
          const newY = clamp(py + (height - newSize), 0, imgH - newSize);
          return { x: newX, y: newY, width: newSize, height: newSize };
        }
        default:
          return prev;
      }
    });
  };

  const generatePreview = () => {
    if (!renderedImage || !imageRef.current) return;

    try {
      const canvas = document.createElement("canvas");
      const image = imageRef.current;
      const {
        width: renderedW,
        height: renderedH,
        naturalWidth,
        naturalHeight,
      } = renderedImage;

      const scaleX = naturalWidth / renderedW;
      const scaleY = naturalHeight / renderedH;

      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");

      ctx.drawImage(
        image,
        cropRect.x * scaleX,
        cropRect.y * scaleY,
        cropRect.width * scaleX,
        cropRect.height * scaleY,
        0,
        0,
        size,
        size
      );

      setCroppedImage(canvas.toDataURL("image/jpeg"));
    } catch (error) {
      console.warn("Canvas preview generation failed:", error);
    }
  };

  useEffect(() => {
    if (imageSrc && renderedImage) generatePreview();
  }, [cropRect]);

  const handleSave = async () => {
    const blob = await fetch(croppedImage).then((res) => res.blob());
    const uploadedUrl = await uploadImage(blob);
    setCroppedImage(uploadedUrl);
    onImageChange(uploadedUrl);
    setDialogOpen(false);
  };

  const uploadImage = async (blob) => {
    const formData = new FormData();
    formData.append("file", blob, "avatar.jpg");
    setIsSubmit(true);
    try {
      const uploadRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/upload/compound_logo`,
        formData
      );
      return uploadRes.data;
    } catch (err) {
      console.error("Failed to upload image", err);
    } finally {
      setIsSubmit(false);
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setImageSrc(initialImage);
    setCroppedImage(initialImage);
  };

  return (
    <Box
      position="relative"
      width={size}
      height={size}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Avatar
        src={croppedImage}
        sx={{
          ...getAvatarSx(),
          border: "2px solid #ccc",
          transition: "box-shadow 0.2s",
          "&:hover": { boxShadow: 5 },
        }}
      />
      <Fade in={hovered}>
        <Box
          sx={{
            position: "absolute",
            right: 8,
            bottom: 8,
            bgcolor: "primary.main",
            borderRadius: "50%",
            boxShadow: 3,
            zIndex: 2,
          }}
        >
          <Tooltip title="Change photo">
            <IconButton
              size="small"
              onClick={openFileSelector}
              sx={{
                position: "absolute",
                right: 0,
                bottom: 0,
                // Move the button out to straddle the border using translate
                transform: "translate(30%, 30%)",
                bgcolor: "primary.main",
                backgroundColor: "primary.main",
                border: "1px solid #fff",
              }}
            >
              <Edit fontSize="small" color="info" />
            </IconButton>
          </Tooltip>
        </Box>
      </Fade>
      {/* <IconButton
        size="small"
        onClick={openFileSelector}
        sx={{
          position: "absolute",
          bottom: 10,
          right: 10,
          backgroundColor: "primary.main",
          border: "1px solid #fff",
        }}
      >
        <Edit fontSize="small" color="info" />
      </IconButton> */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFileChange}
      />

      <Dialog open={dialogOpen} onClose={handleCancel} maxWidth="md">
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "1.1rem",
            bgcolor: colors.primary[600],
            color: "#fff",
            py: 1.5,
            px: 3,
          }}
        >
          Edit Profile
        </DialogTitle>
        <DialogContent sx={{ display: "flex", gap: 3 }}>
          <Box display="flex" justifyContent="center" flexGrow={1} p={2}>
            <Box
              width={containerSize.width}
              height={containerSize.height}
              sx={{ position: "relative", bgcolor: "#000" }}
              onMouseMove={handleMouseMove}
              onMouseUp={stopDragging}
              onMouseLeave={stopDragging}
            >
              {imageSrc && (
                <img
                  ref={imageRef}
                  src={imageSrc}
                  crossOrigin="anonymous"
                  alt="To Crop"
                  style={{
                    width: containerSize.width,
                    height: "auto",
                    display: "block",
                    userSelect: "none",
                  }}
                  onLoad={(e) => {
                    const { naturalWidth, naturalHeight } = e.target;
                    const fixedWidth = 400;
                    const aspectRatio = naturalHeight / naturalWidth;
                    const dynamicHeight = Math.round(fixedWidth * aspectRatio);

                    setContainerSize({
                      width: fixedWidth,
                      height: dynamicHeight,
                    });

                    setRenderedImage({
                      width: fixedWidth,
                      height: dynamicHeight,
                      naturalWidth,
                      naturalHeight,
                    });

                    const initialSize = Math.max(fixedWidth * 0.5, 50);
                    setCropRect({
                      x: (fixedWidth - initialSize) / 2,
                      y: (dynamicHeight - initialSize) / 2,
                      width: initialSize,
                      height: initialSize,
                    });
                  }}
                />
              )}

              {/* Crop overlay */}
              <Box
                sx={{
                  position: "absolute",
                  border: "2px solid white",
                  top: cropRect.y,
                  left: cropRect.x,
                  width: cropRect.width,
                  height: cropRect.height,
                  boxShadow: "0 0 0 10000px rgba(0,0,0,0.5)",
                  cursor: "move",
                }}
                onMouseDown={() => startDragging("move")}
              >
                <Box
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    startDragging("resize-br");
                  }}
                  sx={{
                    position: "absolute",
                    width: 12,
                    height: 12,
                    bottom: -6,
                    right: -6,
                    bgcolor: "#fff",
                    borderRadius: "50%",
                    border: "2px solid #000",
                    cursor: "nwse-resize",
                  }}
                />
                <Box
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    startDragging("resize-tl");
                  }}
                  sx={{
                    position: "absolute",
                    width: 12,
                    height: 12,
                    top: -6,
                    left: -6,
                    bgcolor: "#fff",
                    borderRadius: "50%",
                    border: "2px solid #000",
                    cursor: "nwse-resize",
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Box textAlign="center" p={2}>
            <Avatar
              src={croppedImage}
              sx={{ ...getAvatarSx(), border: "2px solid #ccc" }}
            />
            <Button
              variant="text"
              size="small"
              onClick={openFileSelector}
              sx={{ mt: 2 }}
            >
              Change picture
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleSave} disabled={isSubmit}>
            {isSubmit ? "Saving..." : "Save"}
          </Button>
          <Button onClick={handleCancel}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PictureEditor;
